import React, { useCallback, useMemo, useState } from "react";
import { ExternalLink, Eye, MapPin, Pencil, Plus, Power, RefreshCw } from "lucide-react";
import { MessageRow, Mono, TableCard, Td, Th, Toolbar, rowClass, tableClass, tbodyClass, theadClass } from "../ui/DataPage";
import { useManagedList, useStationManagementApi } from "../../hooks/useStationManagement";
import LocationFormModal from "./LocationFormModal";
import { useStatusToggle } from "./StatusToggle";
import {
  Button,
  ConfirmDialog,
  DetailGrid,
  EmptyState,
  EntityStatus,
  FilterSelect,
  IconButton,
  Overlay,
  Pagination,
  SearchInput,
  YesNo,
  formatDate,
  usePagination,
} from "./ui";

const COLS = 13;
const dash = <span className="text-neutral-400">—</span>;
const coord = (v) => (v === null || v === undefined || v === "" ? dash : Number(v).toFixed(6));

export default function LocationsTab({ groups, groupsLoading, onGroupSaved }) {
  const api = useStationManagementApi();
  const fetchAll = useCallback(() => api.listLocations(), [api]);
  const { items: locations, isLoading, error, refetch } = useManagedList(fetchAll, [fetchAll]);

  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [status, setStatus] = useState("all");
  const [commenced, setCommenced] = useState("all");
  const [editing, setEditing] = useState(undefined);
  const [viewing, setViewing] = useState(null);

  const toggle = useStatusToggle({
    entity: "location",
    describe: (l) => ({
      id: l.location_id,
      next: l.status === "active" ? "inactive" : "active",
      label: `Location ${l.location_id}`,
      warning: "Inactive locations are hidden from the customer app's nearby search. Stations keep their own status.",
    }),
    onChanged: () => refetch(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return locations.filter((l) => {
      if (groupFilter !== "all" && l.group_id !== groupFilter) return false;
      if (status !== "all" && l.status !== status) return false;
      if (commenced !== "all" && String(l.commenced) !== commenced) return false;
      if (!q) return true;
      return [l.location_id, l.location_name, l.group_name, l.address_line1, l.address_line2, l.landmark, l.colony, l.city, l.state, l.zip_code].some((v) =>
        String(v || "").toLowerCase().includes(q)
      );
    });
  }, [locations, search, groupFilter, status, commenced]);

  const pager = usePagination(filtered, 10, `${search}|${groupFilter}|${status}|${commenced}`);
  const hasFilters = search || groupFilter !== "all" || status !== "all" || commenced !== "all";

  const groupOptions = [{ value: "all", label: "All groups" }, ...groups.map((g) => ({ value: g.group_id, label: `${g.group_id} · ${g.group_name}` }))];

  return (
    <div className="space-y-4">
      <Toolbar>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <SearchInput value={search} onChange={setSearch} placeholder="Search ID, name, address, city" />
            <FilterSelect label="Group" value={groupFilter} onChange={setGroupFilter} options={groupOptions} />
            <FilterSelect label="Status" value={status} onChange={setStatus} options={[{ value: "all", label: "All statuses" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} />
            <FilterSelect label="Commenced" value={commenced} onChange={setCommenced} options={[{ value: "all", label: "Commenced: any" }, { value: "true", label: "Commenced" }, { value: "false", label: "Not commenced" }]} />
          </div>
          <div className="flex gap-2">
            <Button icon={RefreshCw} onClick={refetch} disabled={isLoading}>Refresh</Button>
            <Button variant="primary" icon={Plus} onClick={() => setEditing(null)}>Add location</Button>
          </div>
        </div>
      </Toolbar>

      <TableCard header={<span>{filtered.length} location{filtered.length === 1 ? "" : "s"}</span>} footer={filtered.length > 0 ? <Pagination {...pager} /> : null}>
        <table className={tableClass}>
          <thead className={theadClass}>
            <tr>
              <Th>Location ID</Th>
              <Th>Location name</Th>
              <Th>Group</Th>
              <Th>Address</Th>
              <Th>City</Th>
              <Th>State</Th>
              <Th>Country</Th>
              <Th>ZIP</Th>
              <Th align="right">Latitude</Th>
              <Th align="right">Longitude</Th>
              <Th>Status</Th>
              <Th>Commenced</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody className={tbodyClass}>
            {isLoading && locations.length === 0 ? (
              <MessageRow colSpan={COLS}>Loading locations…</MessageRow>
            ) : error ? (
              <MessageRow colSpan={COLS} tone="error">{error} · <button className="underline" onClick={refetch}>Retry</button></MessageRow>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={COLS}>
                  <EmptyState
                    icon={MapPin}
                    title={hasFilters ? "No locations match these filters" : "No locations yet"}
                    description={hasFilters ? "Try a different search or clear the filters." : "Locations hold the address and coordinates of a site. Each belongs to one group."}
                    action={!hasFilters && <Button variant="primary" icon={Plus} onClick={() => setEditing(null)}>Add location</Button>}
                  />
                </td>
              </tr>
            ) : (
              pager.slice.map((l) => (
                <tr key={l.location_id} className={rowClass}>
                  <Td><Mono>{l.location_id}</Mono></Td>
                  <Td className="font-medium text-neutral-900">{l.location_name || dash}</Td>
                  <Td>
                    <span className="text-neutral-900">{l.group_name || "—"}</span>
                    <span className="ml-1 text-xs text-neutral-400">#{l.group_id}</span>
                  </Td>
                  <Td><span className="block max-w-[240px] truncate" title={[l.address_line1, l.address_line2].filter(Boolean).join(", ")}>{l.address_line1}</span></Td>
                  <Td>{l.city}</Td>
                  <Td>{l.state}</Td>
                  <Td>{l.country}</Td>
                  <Td className="tabular-nums">{l.zip_code || dash}</Td>
                  <Td align="right" className="tabular-nums">{coord(l.latitude)}</Td>
                  <Td align="right" className="tabular-nums">{coord(l.longitude)}</Td>
                  <Td><EntityStatus status={l.status} /></Td>
                  <Td><YesNo value={l.commenced} yes="Yes" no="No" /></Td>
                  <Td align="right">
                    <div className="flex justify-end gap-0.5">
                      <IconButton icon={Eye} label="View details" onClick={() => setViewing(l)} />
                      <IconButton icon={Pencil} label="Edit location" onClick={() => setEditing(l)} />
                      <IconButton icon={Power} label={l.status === "active" ? "Deactivate" : "Activate"} onClick={() => toggle.request(l)} className={l.status === "active" ? "hover:!text-red-600" : "hover:!text-green-700"} />
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>

      <LocationFormModal
        open={editing !== undefined}
        location={editing}
        groups={groups}
        groupsLoading={groupsLoading}
        defaultGroupId={groupFilter !== "all" ? groupFilter : ""}
        onClose={() => setEditing(undefined)}
        onSaved={() => refetch()}
        onGroupCreated={onGroupSaved}
      />

      <Overlay
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        variant="drawer"
        width="max-w-lg"
        title={viewing ? `Location ${viewing.location_id}` : ""}
        subtitle={viewing?.location_name}
        footer={
          viewing && (
            <Button
              variant="primary"
              icon={Pencil}
              onClick={() => {
                setEditing(viewing);
                setViewing(null);
              }}
            >
              Edit location
            </Button>
          )
        }
      >
        {viewing && (
          <DetailGrid
            items={[
              ["Location ID", viewing.location_id],
              ["Status", <EntityStatus status={viewing.status} />],
              ["Group", `${viewing.group_id} · ${viewing.group_name}`, true],
              ["Address line 1", viewing.address_line1, true],
              ["Address line 2", viewing.address_line2, true],
              ["Landmark", viewing.landmark],
              ["Colony", viewing.colony],
              ["City", viewing.city],
              ["State", viewing.state],
              ["Country", viewing.country],
              ["ZIP code", viewing.zip_code],
              ["Latitude", viewing.latitude ?? ""],
              ["Longitude", viewing.longitude ?? ""],
              ["Commenced", <YesNo value={viewing.commenced} yes="Yes" no="No" />],
              ["Charging stations", viewing.station_count],
              ["Created", formatDate(viewing.created_at)],
              [
                "Map",
                viewing.latitude != null && viewing.longitude != null ? (
                  <a
                    className="inline-flex items-center gap-1 text-neutral-900 underline-offset-4 hover:underline"
                    href={`https://www.google.com/maps?q=${viewing.latitude},${viewing.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Google Maps <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  ""
                ),
              ],
            ]}
          />
        )}
      </Overlay>

      <ConfirmDialog {...toggle.dialogProps} />
    </div>
  );
}
